{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../../src/Layout';
export default Layout;
import {FunctionAPIGroup} from '../../../src/FunctionAPI';
import {InterfaceType} from '../../../src/types';
import docs from 'docs:@react-aria/combobox';
import statelyDocs from 'docs:@react-stately/combobox';
import Anatomy from '../ComboBoxAnatomy.svg';

export const section = 'Hooks';
export const description = 'Provides the behavior and accessibility implementation for a combo box component.';
export const isSubpage = true;

# useComboBox

<PageDescription>{docs.exports.useComboBox.description}</PageDescription>

```tsx render files={["starters/hooks/src/ComboBox.tsx", "starters/hooks/src/DropdownListBox.tsx", "starters/hooks/src/ComboBox.css", "starters/hooks/src/Form.css", "starters/hooks/src/ListBox.css", "starters/hooks/src/Popover.css"]}
"use client";
import {ComboBox, ComboBoxItem} from 'hooks-starter/ComboBox';

<ComboBox label="Favorite Animal">
  <ComboBoxItem>Aardvark</ComboBoxItem>
  <ComboBoxItem>Cat</ComboBoxItem>
  <ComboBoxItem>Dog</ComboBoxItem>
  <ComboBoxItem>Kangaroo</ComboBoxItem>
  <ComboBoxItem>Panda</ComboBoxItem>
  <ComboBoxItem>Snake</ComboBoxItem>
</ComboBox>
```

## API

<Anatomy />

```tsx
<ComboBox>
  <Label />
  <Group>
    <Input />
    <Button />
  </Group>
  <ComboBoxValue />
  <Text slot="description" />
  <FieldError />
  <Popover>
    <ListBox />
  </Popover>
</ComboBox>
```

<FunctionAPIGroup
  functions={[
    {function: statelyDocs.exports.useComboBoxState, links: statelyDocs.links},
    {function: docs.exports.useComboBox, links: docs.links},
  ]}
/>

### ComboBoxState

<InterfaceType {...statelyDocs.exports.ComboBoxState} links={statelyDocs.links} />

### AriaComboBoxOptions

<InterfaceType {...docs.exports.AriaComboBoxOptions} />

### ComboBoxAria

<InterfaceType {...docs.exports.ComboBoxAria} />
